import {
   AppointmentPractitioner,
   HealthcareProviderLocationIdentifier,
} from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { TimeOfDay, WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';
import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';
import { ServiceLocationId, ServiceLocationType } from '@dreryk/egabinet-healthcare-provider-published-language-api';
import { ReceptionServiceForPractitionerSimple } from '@dreryk/egabinet-private-medical-service-frontend-api';

import {
   assertUnreachable,
   commonTranslationKeys,
   convertDateToLocalDate,
   convertDateToLocalTime,
   createSingleInterpolatedTranslationKey,
   TranslateFunction,
} from '@dreryk/react-common';
import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';

import { protectFeature } from '~libs.shared.system';

import { waitlistEntryTranslationKeys } from '../../../../translations';
import { getInitialEstimatedDuration } from '../../shared/form/fields/EstimatedDurationField.utils';
import { AppointmentFormBean } from '../../shared/form/AppointmentForm.types';
import { mapReceptionServiceForPractitionerSimpleToAppointmentReceptionService } from '../../../../receptionService/extensions/calendar/appointment/interactiveTable/ReceptionServiceInteractiveTableSelector.utils';

import { AppointmentWaitlistCreateFormInitializer } from './AppointmentWaitlistCreateForm.types';

type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
const ORDERED_DAYS_OF_WEEK: DayOfWeek[] = [
   'MONDAY',
   'TUESDAY',
   'WEDNESDAY',
   'THURSDAY',
   'FRIDAY',
   'SATURDAY',
   'SUNDAY',
];

const ORDERED_TIMES_OF_DAY: TimeOfDay[] = ['MORNING', 'AFTERNOON', 'EVENING'];

const mapPatientAvailabilityToNote = (
   patientAvailability: WaitlistEntry['patientAvailability'],
   translate: TranslateFunction
): string => {
   return ORDERED_DAYS_OF_WEEK.reduce((acc, dayOfWeek) => {
      const dayPreference = patientAvailability[dayOfWeek];

      if (!dayPreference) {
         return acc;
      }

      const timePreferencesDescription =
         dayPreference.length === ORDERED_TIMES_OF_DAY.length
            ? translate(waitlistEntryTranslationKeys.noRestrictions)
            : ORDERED_TIMES_OF_DAY.reduce((timesOfDayAcc, timeOfDay) => {
                 if (!dayPreference.includes(timeOfDay)) {
                    return timesOfDayAcc;
                 }
                 const timeOfDayDescription = translate(waitlistEntryTranslationKeys.timeOfDayMap[timeOfDay]);
                 return timesOfDayAcc ? `${timesOfDayAcc}, ${timeOfDayDescription}` : timeOfDayDescription;
              }, '');

      return `${acc}${translate(
         commonTranslationKeys.dayOfWeekAbbreviationMap[dayOfWeek]
      )} ${timePreferencesDescription}, `;
   }, '');
};

type AppointmentWaitlistFormInitializerOptions = {
   selectedSlot: TimetableSlotSelectionEvent<AppointmentPractitioner>;
   healthcareLocationProviders: HealthcareProviderLocation[];
   healthcareProviderLocationIdentifierFromSchedule: HealthcareProviderLocationIdentifier | null;
   waitlistEntry: WaitlistEntry;
   translate: TranslateFunction;
   practitioners: AppointmentPractitioner[];
   patient: PatientSimple | undefined;
   receptionService: ReceptionServiceForPractitionerSimple | undefined;
};

/* eslint-disable max-lines-per-function */
export const appointmentWaitlistCreateFormInitializer = (
   options: AppointmentWaitlistFormInitializerOptions
): AppointmentWaitlistCreateFormInitializer => () => {
   const {
      waitlistEntry,
      healthcareProviderLocationIdentifierFromSchedule,
      healthcareLocationProviders,
      selectedSlot,
      translate,
      practitioners,
      patient,
      receptionService,
   } = options;

   const getHealthcareProviderLocationFromSchedule = (): HealthcareProviderLocation | null => {
      return (
         healthcareLocationProviders.find(
            (location) =>
               location.locationIdentifier.locationId ===
                  healthcareProviderLocationIdentifierFromSchedule?.locationId &&
               location.locationIdentifier.type === healthcareProviderLocationIdentifierFromSchedule.type
         ) ?? null
      );
   };

   const getHealthcareProviderLocationFromWaitlistEntry = (): HealthcareProviderLocation | null => {
      return (
         healthcareLocationProviders.find(
            (location) =>
               ServiceLocationId.of(
                  location.locationIdentifier.type as ServiceLocationType,
                  location.locationIdentifier.locationId
               ).asString() === waitlistEntry?.location?.id?.asString()
         ) ?? null
      );
   };

   const getHealthcareProviderLocationIfSingle = (): HealthcareProviderLocation | null => {
      if (healthcareLocationProviders.length === 1) {
         return healthcareLocationProviders[0];
      }

      return null;
   };

   const startTime = convertDateToLocalTime(selectedSlot.start);
   const endTime = convertDateToLocalTime(selectedSlot.end);
   const date = convertDateToLocalDate(selectedSlot.start);

   const healthcareProviderLocation =
      getHealthcareProviderLocationFromSchedule() ?? getHealthcareProviderLocationFromWaitlistEntry();

   const waitlistNotePart = waitlistEntry.note ? `${waitlistEntry.note}\n\n` : '';

   const patientAvailabilityNotePart = `${translate(
      waitlistEntryTranslationKeys.datePreferences
   )} ${mapPatientAvailabilityToNote(waitlistEntry.patientAvailability, translate)}`;

   const priorityLabel = translate(
      waitlistEntryTranslationKeys.reservationPreferenceMap[waitlistEntry.reservationPreference]
   );

   const priorityNotePart = translate(
      createSingleInterpolatedTranslationKey(waitlistEntryTranslationKeys.priorityLabelled, 'label', priorityLabel)
   );

   const note = `${waitlistNotePart}${patientAvailabilityNotePart}${priorityNotePart}`;

   const searchingPractitionersEnabled = protectFeature('searchingAllPractitioners', true, false);
   const practitioner =
      waitlistEntry && searchingPractitionersEnabled
         ? practitioners.find((practitioner) => practitioner.id.asNumber() === waitlistEntry.practitioner.id.asNumber())
         : selectedSlot.resource;

   return {
      healthcareProviderLocation: healthcareProviderLocation ?? getHealthcareProviderLocationIfSingle(),
      practitioner: practitioner ?? selectedSlot.resource,
      startTime: startTime,
      endTime: endTime,
      date: date,
      dateConfirmedWithPatient: false,
      estimatedDuration: getInitialEstimatedDuration(startTime, endTime, date),
      overlappingStrategy: 'SKIP_OVERLAPPING_DATES',
      patient: patient ?? null,
      note: note,
      receptionService: {
         selector: [],
         value: receptionService
            ? [
                 {
                    receptionService: mapReceptionServiceForPractitionerSimpleToAppointmentReceptionService(
                       receptionService
                    ),
                 },
              ]
            : [],
      },
      videoConsultation: waitlistEntry.encounterMode === 'VIDEO_CONSULTATION',
   };
};

type IncompatiblePreferenceFormBean = Pick<
   AppointmentFormBean,
   'healthcareProviderLocation' | 'videoConsultation' | 'practitioner' | 'receptionService'
>;

type PreferenceName = 'location' | 'encounterMode' | 'practitioner' | 'service';
export const createIncompatiblePreferenceNotificationChecker = (
   formBean: IncompatiblePreferenceFormBean,
   waitlistEntry: WaitlistEntry | null
) => (preferenceName: PreferenceName): boolean => {
   if (!waitlistEntry || !formBean) {
      return false;
   }

   switch (preferenceName) {
      case 'location':
         return Boolean(
            waitlistEntry.location &&
               formBean.healthcareProviderLocation &&
               ServiceLocationId.of(
                  formBean.healthcareProviderLocation.locationIdentifier.type as ServiceLocationType,
                  formBean.healthcareProviderLocation.locationIdentifier.locationId
               ).asString() !== waitlistEntry.location.id.asString()
         );
      case 'encounterMode':
         return formBean.videoConsultation !== (waitlistEntry.encounterMode === 'VIDEO_CONSULTATION');
      case 'practitioner':
         if (!formBean.practitioner) {
            return false;
         }
         return formBean.practitioner.id.asNumber() !== waitlistEntry.practitioner.id.asNumber();
      case 'service':
         if (!waitlistEntry.receptionService) {
            return false;
         }
         return Boolean(
            !formBean.receptionService?.value?.length ||
               (waitlistEntry.receptionService &&
                  formBean.receptionService?.value?.every(
                     (selectedReceptionService) =>
                        selectedReceptionService.receptionService.receptionServiceId !==
                        waitlistEntry.receptionService?.id.asNumber()
                  ))
         );
      default:
         return assertUnreachable(preferenceName);
   }
};

type PriorityPreferenceType = 'practitioner' | 'location' | 'time';
export const createPriorityPreferenceChecker = (waitlistEntry: WaitlistEntry) => (
   preferenceType: PriorityPreferenceType
) => {
   switch (preferenceType) {
      case 'practitioner':
         return waitlistEntry.reservationPreference === 'PRACTITIONER';
      case 'location':
         return waitlistEntry.reservationPreference === 'LOCATION';
      case 'time':
         return waitlistEntry.reservationPreference === 'TIME';
      default:
         return assertUnreachable(preferenceType);
   }
};
